import React, { useState } from "react";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { Mail, Clock, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

const TOPICS = ["General Inquiry","Account Issue","Payment / Billing","Certification & Badge","Course Question","Technical Support","Partnership / Business","Other"];

const CONTACTS = [
  { label:"General Support",    value:"support@pilotlinkusa.org",  href:"mailto:support@pilotlinkusa.org",  sub:"Response within 24 hours" },
  { label:"Billing & Payments", value:"billing@pilotlinkusa.org",  href:"mailto:billing@pilotlinkusa.org",  sub:"Stripe transaction help" },
  { label:"Certifications",     value:"verify@pilotlinkusa.org",   href:"mailto:verify@pilotlinkusa.org",   sub:"Badge & credential questions" },
  { label:"Business Hours",     value:"Mon–Fri, 8AM–6PM CST",      href:null,                               sub:"Excluding US federal holidays" },
];
const FAQ = [
  [
    "How do I get my Verified Badge?",
    "Pass the PPCA exam. Your results are uploaded automatically and your Verified PPCA Badge is added to your profile with a unique QR code for employer verification."
  ],
  [
    "My OTP email isn't arriving?",
    "Check your spam folder first. Then use 'Resend OTP' on the verification screen. If the issue continues, email support@pilotlinkusa.org."
  ],
  [
    "How long does the Crash Course take?",
    "Crash Course access follows your active certification cycle. Users study all required states at their own pace, and completion time varies depending on experience and familiarity with state regulations. Course content is updated regularly as state laws change."
  ],
  [
    "What happens after I pass my State Exam?",
    "Upload your certification and expiration date to activate your learning cycle. You will continue to access updated Crash Course materials until your certification expires. No quiz retakes are required after passing."
  ],
  [
    "How does employer verification work?",
    "Your Verified PPCA Badge includes a QR code that employers can scan to confirm your certification status instantly through your verification page."
  ]
];

function FaqItem({ q, a, isDark }) {
  const [open, setOpen] = useState(false);
  const text    = isDark ? "#f0f4f8" : "#0d1f3c";
  const textMid = isDark ? "rgba(240,244,248,0.80)" : "#334155";
  const border  = isDark ? "rgba(255,255,255,0.10)" : "#e2e8f0";

  return (
    <div style={{ borderBottom:`1px solid ${border}` }}>
      <button onClick={() => setOpen(p => !p)}
        style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, padding:"14px 0", background:"none", border:"none", cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}>
        <span style={{ fontSize:14, fontWeight:600, color:text, lineHeight:1.5 }}>{q}</span>
        <ChevronRight size={16} style={{ color:"#c9971c", flexShrink:0, transform:open?"rotate(90deg)":"none", transition:"transform .2s" }}/>
      </button>
      {open && <p style={{ fontSize:13.5, color:textMid, lineHeight:1.80, margin:"0 0 14px" }}>{a}</p>}
    </div>
  );
}

export default function ContactUs({ isDarkMode, toggleTheme }) {
  const D       = isDarkMode;
  const bg      = D ? "#0d1f3c" : "#f4f7fb";
  const panel   = D ? "#142a4d" : "#ffffff";
  const mid     = D ? "#1a3358" : "#f0f4f8";
  const border  = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text    = D ? "#f0f4f8" : "#0d1f3c";
  const textSub = D ? "rgba(240,244,248,0.55)" : "#64748b";
  const inputBg = D ? "rgba(255,255,255,0.06)" : "#f8fafc";
  const gold    = "#c9971c";
  const goldL   = "#fbbf24";

  const [form,   setForm]   = useState({ name:"", email:"", topic:"General Inquiry", message:"" });
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/contact/`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify(form),
      });
      setStatus(res.ok ? "sent" : "error");
    } catch {
      window.location.href = `mailto:support@pilotlinkusa.org?subject=${encodeURIComponent("["+form.topic+"] "+form.name)}&body=${encodeURIComponent(form.message)}`;
      setStatus("sent");
    }
  };

  const inp = { width:"100%", padding:"11px 14px", borderRadius:10, border:`1.5px solid ${border}`, background:inputBg, color:text, fontSize:14, fontFamily:"inherit", outline:"none", boxSizing:"border-box", transition:"border-color .15s" };

  return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"'Inter',sans-serif", transition:"background .3s" }}>
      <Navbar isDarkMode={D} toggleTheme={toggleTheme}/>

      <div style={{ background:"linear-gradient(135deg,#0d1f3c,#1a3358)", padding:"clamp(40px,6vw,68px) 16px", textAlign:"center", borderBottom:`3px solid ${gold}` }}>
        <div style={{ width:52, height:52, borderRadius:13, background:gold, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
          <Mail size={26} style={{ color:"#0d1f3c" }}/>
        </div>
        <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(24px,4vw,38px)", fontWeight:900, color:"#fff", margin:"0 0 10px" }}>Contact Us</h1>
        <p style={{ fontSize:15, color:"rgba(255,255,255,0.60)", margin:"0 auto", maxWidth:460, lineHeight:1.7 }}>
          The PilotLink USA team is here to help. We respond within 24 hours on business days.
        </p>
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"clamp(24px,4vw,44px) clamp(14px,3vw,24px)" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,420px),1fr))", gap:"clamp(14px,2vw,24px)" }}>

          {/* Left */}
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div style={{ background:panel, borderRadius:16, padding:"clamp(18px,2.5vw,24px)", border:`1px solid ${border}` }}>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:18, fontWeight:800, color:text, margin:"0 0 16px" }}>Get in Touch</h2>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {CONTACTS.map((c, i) => (
                  <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start", padding:"12px 14px", borderRadius:11, background:mid, border:`1px solid ${border}` }}>
                    <div style={{ width:40, height:40, borderRadius:10, flexShrink:0, background:D?"rgba(201,151,28,0.15)":"#fef9ec", border:"1.5px solid rgba(201,151,28,0.25)", display:"flex", alignItems:"center", justifyContent:"center", color:gold }}>
                      {i < 3 ? <Mail size={18}/> : <Clock size={18}/>}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:textSub, textTransform:"uppercase", letterSpacing:".06em" }}>{c.label}</div>
                      {c.href
                        ? <a href={c.href} style={{ fontSize:14, fontWeight:700, color:gold, textDecoration:"none", display:"block", marginTop:3 }}>{c.value}</a>
                        : <div style={{ fontSize:14, fontWeight:700, color:text, marginTop:3 }}>{c.value}</div>
                      }
                      <div style={{ fontSize:12, color:textSub, marginTop:2 }}>{c.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background:panel, borderRadius:16, padding:"clamp(18px,2.5vw,24px)", border:`1px solid ${border}` }}>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:18, fontWeight:800, color:text, margin:"0 0 16px" }}>FAQs</h2>
              {FAQ.map(([q, a], i) => <FaqItem key={i} q={q} a={a} isDark={D}/>)}
            </div>
          </div>

          {/* Right — Form */}
          <div style={{ background:panel, borderRadius:16, padding:"clamp(18px,2.5vw,28px)", border:`1px solid ${border}`, height:"fit-content" }}>
            <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:18, fontWeight:800, color:text, margin:"0 0 4px" }}>Send a Message</h2>
            <p style={{ fontSize:13, color:textSub, marginBottom:20 }}>We'll get back to you within 1 business day.</p>

            {status === "sent" ? (
              <div style={{ textAlign:"center", padding:"40px 20px" }}>
                <div style={{ fontSize:48, marginBottom:16 }}>✅</div>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:20, fontWeight:800, color:text, marginBottom:8 }}>Message Sent!</div>
                <p style={{ fontSize:14, color:textSub, lineHeight:1.75 }}>We'll respond to <strong style={{ color:text }}>{form.email}</strong> within 1 business day.</p>
                <button onClick={() => { setStatus(null); setForm({name:"",email:"",topic:"General Inquiry",message:""}); }}
                  style={{ marginTop:20, padding:"10px 24px", borderRadius:99, border:"none", background:`linear-gradient(135deg,${gold},${goldL})`, color:"#0d1f3c", fontWeight:800, fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                    <div>
                      <label style={{ fontSize:11, fontWeight:700, color:textSub, display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:".06em" }}>Name *</label>
                      <input required value={form.name} onChange={e => setForm(p => ({...p, name:e.target.value}))} placeholder="Your full name" style={inp}
                        onFocus={e => e.target.style.borderColor=gold} onBlur={e => e.target.style.borderColor=border}/>
                    </div>
                    <div>
                      <label style={{ fontSize:11, fontWeight:700, color:textSub, display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:".06em" }}>Email *</label>
                      <input required type="email" value={form.email} onChange={e => setForm(p => ({...p, email:e.target.value}))} placeholder="your@email.org" style={inp}
                        onFocus={e => e.target.style.borderColor=gold} onBlur={e => e.target.style.borderColor=border}/>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize:11, fontWeight:700, color:textSub, display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:".06em" }}>Topic</label>
                    <select value={form.topic} onChange={e => setForm(p => ({...p, topic:e.target.value}))} style={inp}>
                      {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:11, fontWeight:700, color:textSub, display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:".06em" }}>Message *</label>
                    <textarea required rows={5} value={form.message} onChange={e => setForm(p => ({...p, message:e.target.value}))} placeholder="Describe your question or issue…"
                      style={{ ...inp, resize:"vertical", minHeight:120 }}
                      onFocus={e => e.target.style.borderColor=gold} onBlur={e => e.target.style.borderColor=border}/>
                  </div>
                  {status === "error" && (
                    <div style={{ padding:"10px 14px", borderRadius:9, background:D?"rgba(220,38,38,0.12)":"#fff1f2", border:"1px solid rgba(220,38,38,0.30)", fontSize:13, color:D?"#fca5a5":"#dc2626" }}>
                      Something went wrong. Please email support@pilotlinkusa.org directly.
                    </div>
                  )}
                  <button type="submit" disabled={status==="sending"}
                    style={{ padding:"14px", borderRadius:11, border:"none", background:status==="sending"?"rgba(201,151,28,0.40)":`linear-gradient(135deg,${gold},${goldL})`, color:"#0d1f3c", fontWeight:900, fontSize:15, cursor:status==="sending"?"not-allowed":"pointer", fontFamily:"inherit", boxShadow:status!=="sending"?`0 4px 16px rgba(201,151,28,0.40)`:"none", transition:"all .15s" }}>
                    {status === "sending" ? "Sending…" : "Send Message →"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer isDarkMode={D}/>
    </div>
  );
}
